import { atom } from "jotai";
import { atomWithQuery, queryClientAtom } from "jotai-tanstack-query";
import { type Tag } from "tag-api-client";
import { tagApiService } from "../services/tagApiService";

// Query atom - fetches tags with React Query caching
export const tagsQueryAtom = atomWithQuery(() => ({
    queryKey: ["tags"],
    queryFn: async () => {
        console.log("[tagsQueryAtom] Fetching tags from API");
        const tags = await tagApiService.listTags();
        console.log("[tagsQueryAtom] Fetched", tags.length, "tags");
        return tags;
    },
    staleTime: Infinity, // Cache forever until manually invalidated
}));

// Write-only atom to refetch tags
export const refetchTagsAtom = atom(null, async (get, set) => {
    console.log("[refetchTagsAtom] Invalidating tags cache");
    const queryClient = get(queryClientAtom);
    await queryClient.invalidateQueries({ queryKey: ["tags"] });
});

// Derived atom: extract tags data
export const tagsAtom = atom((get) => {
    const query = get(tagsQueryAtom);
    return query.data ?? [];
});

// Derived atom: sorted tags
export const sortedTagsAtom = atom((get) => {
    const tags = get(tagsAtom);
    return [...tags].sort((a, b) => a.keyValue.localeCompare(b.keyValue));
});

// Derived atom: unique tag keys
export const tagKeysAtom = atom((get) => {
    const tags = get(tagsAtom);
    const keys = new Set<string>();
    tags.forEach((tag) => {
        const keyPart = tag.keyValue.split(":")[0];
        if (keyPart) keys.add(keyPart);
    });
    return Array.from(keys).sort();
});

// Derived atom: tags grouped by key
export const tagsByKeyAtom = atom((get) => {
    const tags = get(tagsAtom);
    const tagsByKey: Record<string, Tag[]> = {};

    tags.forEach((tag) => {
        const keyPart = tag.keyValue.split(":")[0];
        if (!keyPart) return;

        if (!tagsByKey[keyPart]) {
            tagsByKey[keyPart] = [];
        }
        tagsByKey[keyPart].push(tag);
    });

    Object.keys(tagsByKey).forEach((key) => {
        tagsByKey[key].sort((a, b) => {
            const aValue = a.keyValue.split(":")[1] || "";
            const bValue = b.keyValue.split(":")[1] || "";
            return aValue.localeCompare(bValue);
        });
    });

    return tagsByKey;
});

// Derived atom: tagId -> keyValue map
export const tagIdToKeyValueMapAtom = atom((get) => {
    const tags = get(tagsAtom);
    return Object.fromEntries(tags.map((tag) => [tag.tagId, tag.keyValue]));
});
